import React, { useEffect, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogOverlay,
	DialogPortal,
} from "../../../components/ui/dialog";
import { X, BookOpen } from "lucide-react";
import { PromptExamplesModal } from "./PromptExamplesModal";

export const OzapAISettingsDialog = ({
	isOpen,
	onClose,
	prompt,
	instanceId,
	onSave,
}) => {
	const [localPrompt, setLocalPrompt] = useState(prompt);
	const [isExamplesModalOpen, setIsExamplesModalOpen] = useState(false);

	const handleSave = () => {
		onSave({
			prompt: localPrompt,
		});
	};

	useEffect(() => {
		if (isOpen) {
			setLocalPrompt(prompt || "");
		}
	}, [isOpen, prompt]);

	return (
		<>
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogPortal>
					<DialogOverlay className="z-[8999] bg-black/80 backdrop-blur-md" />
					<DialogContent
						className="max-w-[850px] bg-gradient-to-br from-modalBg via-modalBgSecondary to-modalBg backdrop-blur-3xl border border-modalBorder shadow-2xl shadow-black/50 relative overflow-hidden"
						style={{
							position: 'fixed',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 9001
						}}
					>
					<div className="absolute inset-0 bg-gradient-to-br from-modalOverlaySoft via-transparent to-modalOverlayStrong opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none"></div>
					<div className="absolute inset-0 bg-gradient-to-r from-transparent via-modalOverlayStrong to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1500 pointer-events-none"></div>
					<div className="absolute top-16 left-24 w-40 h-40 bg-emeraldBase/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '8s' }}></div>
					<div className="absolute bottom-24 right-20 w-48 h-48 bg-cyanPrimary/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '9s', animationDelay: '2s' }}></div>
					<div className="absolute top-40 right-40 w-28 h-28 bg-tealPrimary/20 rounded-full blur-2xl animate-pulse pointer-events-none" style={{ animationDuration: '6s', animationDelay: '4s' }}></div>
					<div className="absolute bottom-16 left-16 w-36 h-36 bg-emeraldMedium/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: '7s', animationDelay: '3s' }}></div>

					<button
						onClick={onClose}
						className="absolute right-4 top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full bg-modalOverlayMedium backdrop-blur-xl border border-modalOverlayStrong text-textOnDarkMedium hover:text-white hover:bg-modalOverlayStrong transition-all duration-300 hover:scale-110"
					>
						<X className="w-4 h-4" />
					</button>

					<DialogHeader className="relative z-10">
						<DialogTitle className="flex items-center gap-4 text-white">
							<div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emeraldBase/80 via-cyanPrimary/30 to-tealPrimary/40 shadow-lg border border-white/20">
								<span className="text-xl drop-shadow-sm">⚡</span>
								<div className="absolute inset-0 bg-gradient-to-br from-emeraldBase/10 to-cyanPrimary/8 rounded-xl animate-pulse-subtle"></div>
							</div>
							<div>
								<span className="text-xl font-semibold">Configurações oZap AI</span>
								<p className="text-sm text-emeraldMedium font-normal mt-0.5">Acesso ilimitado • Sem necessidade de chave API</p>
							</div>
						</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-6 relative z-10 overflow-y-auto max-h-[calc(100vh-220px)] pr-2">

						<div className="rounded-lg bg-emeraldBase/10 border border-emeraldMedium/30 p-4">
							<div className="flex items-start">
								<div className="flex-shrink-0">
									<svg className="h-5 w-5 text-emeraldBase" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
									</svg>
								</div>
								<div className="ml-3 flex-1">
									<h3 className="text-sm font-medium text-emeraldBase">
										oZap AI - Disponível Ilimitado
									</h3>
									<div className="mt-2 text-sm text-textSecondary">
										<p>
											O oZap AI é um provider exclusivo com acesso ilimitado e sem custos. <br/>
											Não requer chave API - é por nossa conta! 🚀
										</p>
									</div>
								</div>
							</div>
						</div>

						<div>
							<div className="flex items-center justify-between mb-2">
								<label
									htmlFor="ozap-prompt"
									className="block text-sm font-medium text-textOnDarkStrong"
								>
									Prompt do oZap AI
								</label>
								<button
									type="button"
									onClick={() => setIsExamplesModalOpen(true)}
									className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emeraldBase/20 to-cyanPrimary/20 hover:from-emeraldBase/30 hover:to-cyanPrimary/30 border border-emeraldBase/30 text-emeraldMedium hover:text-white transition-all duration-300 hover:scale-105 text-xs font-medium"
								>
									<BookOpen className="w-3.5 h-3.5" />
									<span>Exemplos de Prompts</span>
								</button>
							</div>
							<textarea
								id="ozap-prompt"
								className="w-full h-40 rounded-lg border border-emerald-500/25 bg-modalOverlaySoft backdrop-blur-xl p-4 text-white focus:border-emerald-400 focus:ring focus:ring-emerald-400/30 shadow-lg placeholder-gray-400"
								value={localPrompt}
								onChange={(e) => setLocalPrompt(e.target.value)}
								placeholder="Você é um assistente útil..."
							/>
						</div>

						<div className="flex justify-end items-center pt-4 mt-2 relative z-10">
							<div className="flex space-x-3">
								<button
									type="button"
									onClick={onClose}
									className="relative rounded-xl text-white px-4 py-3 font-medium transition-all duration-300 bg-gradient-to-r from-dangerFrom to-dangerTo hover:from-red-400 hover:to-red-500 shadow-lg hover:shadow-red-400/25 hover:scale-[1.02] cursor-pointer"
								>
									<span className="relative z-10">Cancelar</span>
								</button>
								<button
									onClick={handleSave}
									type="button"
									className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-600/80 to-cyan-600/80 backdrop-blur-xl border border-emerald-400/60 px-4 py-3 text-sm font-semibold text-white transition-all duration-300 hover:from-emerald-500/90 hover:to-cyan-500/90 hover:border-emerald-300/70 hover:shadow-xl hover:shadow-emerald-500/40 shadow-lg shadow-emerald-500/25 hover:scale-[1.02]"
								>
									<svg className="h-5 w-5 drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
									</svg>
									<span>Salvar</span>
								</button>
							</div>
						</div>
					</div>
					</DialogContent>
				</DialogPortal>
			</Dialog>

			<PromptExamplesModal
				isOpen={isExamplesModalOpen}
				onClose={() => setIsExamplesModalOpen(false)}
			/>
		</>
	);
};
